"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { apiGet, apiPost } from "@/lib/api";

export type User = {
  username: string;
  email: string;
  roles: string[];
  debeCambiarPassword: boolean;
};

type Msg = { message: string };

// El backend (/auth/me y /auth/login) devuelve {email, nombre, roles, debe_cambiar_password, ...}.
function toUser(d: unknown): User | null {
  const o = d as {
    email?: string;
    nombre?: string | null;
    roles?: string[];
    debe_cambiar_password?: boolean;
  };
  if (!o || !o.email) return null;
  return {
    username: o.nombre || o.email,
    email: o.email,
    roles: o.roles ?? [],
    debeCambiarPassword: o.debe_cambiar_password ?? false,
  };
}

type AuthState = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (nombre: string, email: string, password: string, codigoReferido?: string) => Promise<Msg>;
  forgotPassword: (email: string) => Promise<Msg>;
  resetPassword: (token: string, password: string) => Promise<Msg>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => ({ message: "" }),
  forgotPassword: async () => ({ message: "" }),
  resetPassword: async () => ({ message: "" }),
  changePassword: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // La cookie httpOnly (si existe) va sola; preguntamos quién es.
    apiGet("/auth/me")
      .then((d) => setUser(toUser(d)))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    // El backend fija la cookie httpOnly y devuelve el usuario.
    const res = await apiPost<{ user: unknown }>("/auth/login", {
      email,
      password,
      canal: "web",
    });
    setUser(toUser(res.user));
  };

  const register = (nombre: string, email: string, password: string, codigoReferido?: string) =>
    apiPost<Msg>("/auth/register", {
      nombre,
      email,
      password,
      ...(codigoReferido ? { codigo_referido: codigoReferido } : {}),
    });

  const forgotPassword = (email: string) =>
    apiPost<Msg>("/auth/forgot-password", { email });

  const resetPassword = (resetToken: string, password: string) =>
    apiPost<Msg>("/auth/reset-password", { token: resetToken, password });

  const changePassword = async (currentPassword: string, newPassword: string) => {
    // El backend reemite la cookie httpOnly con la sesión ya renovada. Refrescamos el usuario para
    // recoger el flag `debe_cambiar_password` ya en false (cierra el cambio forzado de arranque).
    await apiPost("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    await apiGet("/auth/me")
      .then((d) => setUser(toUser(d)))
      .catch(() => {});
  };

  const logout = async () => {
    await apiPost("/auth/logout", {}).catch(() => {});
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, forgotPassword, resetPassword, changePassword, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
